<!-- B4 机构预约管理 -->
<template>
  <div>
    <el-card shadow="never">
      <el-form ref="queryFormRef" :inline="true" :model="queryParams" class="-mb-15px" label-width="72px">
        <el-form-item label="订单 ID" prop="orderId">
          <el-input
            v-model="queryParams.orderId"
            class="!w-160px"
            clearable
            placeholder="订单 ID"
            @keyup.enter="handleQuery"
          />
        </el-form-item>
        <el-form-item label="门店" prop="storeId">
          <el-select v-model="queryParams.storeId" class="!w-180px" clearable filterable placeholder="全部门店">
            <el-option v-for="s in storeOptions" :key="s.id" :label="s.name" :value="s.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="queryParams.status" class="!w-160px" clearable placeholder="全部">
            <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="联系电话" prop="contactPhone">
          <el-input
            v-model="queryParams.contactPhone"
            class="!w-160px"
            clearable
            placeholder="联系电话"
            @keyup.enter="handleQuery"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleQuery">
            <Icon class="mr-5px" icon="ep:search" />搜索
          </el-button>
          <el-button @click="resetQuery">
            <Icon class="mr-5px" icon="ep:refresh" />重置
          </el-button>
        </el-form-item>
      </el-form>

      <el-table v-loading="loading" :data="list" stripe>
        <el-table-column align="center" label="ID" prop="id" width="72" />
        <el-table-column align="center" label="订单 ID" prop="orderId" width="96" />
        <el-table-column align="center" label="门店" width="120">
          <template #default="{ row }">
            {{ storeNameMap[row.storeId] || row.storeId || '—' }}
          </template>
        </el-table-column>
        <el-table-column align="center" label="预约日期" prop="appointmentDate" width="120" />
        <el-table-column align="center" label="时段" prop="appointmentTime" width="120" />
        <el-table-column align="center" label="联系人" prop="contactName" width="100" />
        <el-table-column align="center" label="电话" prop="contactPhone" width="120" />
        <el-table-column align="center" label="地址" min-width="160" prop="address" show-overflow-tooltip />
        <el-table-column align="center" label="状态" prop="status" width="100">
          <template #default="{ row }">
            <el-tag :type="statusTagType(row.status)" size="small">{{ statusLabel(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column align="center" fixed="right" label="操作" width="180">
          <template #default="{ row }">
            <el-button link type="primary" @click="openDetail(row.id)">详情</el-button>
            <template v-if="!['CANCELLED', 'COMPLETED'].includes(row.status || '')">
              <el-button link type="primary" @click="openReschedule(row)">改期</el-button>
              <el-button link type="danger" @click="handleCancel(row)">取消</el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>

      <Pagination
        v-model:limit="queryParams.pageSize"
        v-model:page="queryParams.pageNo"
        :total="total"
        @pagination="getList"
      />
    </el-card>

    <el-dialog v-model="detailVisible" title="预约详情" width="560px" destroy-on-close>
      <el-descriptions v-loading="detailLoading" :column="1" border>
        <el-descriptions-item label="预约 ID">{{ detail?.id }}</el-descriptions-item>
        <el-descriptions-item label="订单 ID">{{ detail?.orderId }}</el-descriptions-item>
        <el-descriptions-item label="门店">
          {{ storeNameMap[detail?.storeId || 0] || detail?.storeId || '—' }}
        </el-descriptions-item>
        <el-descriptions-item label="预约日期">{{ detail?.appointmentDate }}</el-descriptions-item>
        <el-descriptions-item label="时段">{{ detail?.appointmentTime }}</el-descriptions-item>
        <el-descriptions-item label="联系人">{{ detail?.contactName }}</el-descriptions-item>
        <el-descriptions-item label="电话">{{ detail?.contactPhone }}</el-descriptions-item>
        <el-descriptions-item label="地址">{{ detail?.address }}</el-descriptions-item>
        <el-descriptions-item label="状态">{{ statusLabel(detail?.status) }}</el-descriptions-item>
        <el-descriptions-item label="备注">{{ detail?.remark || '—' }}</el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button @click="detailVisible = false">关 闭</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="rescheduleVisible" title="预约改期" width="480px" destroy-on-close>
      <el-form ref="rescheduleRef" :model="rescheduleForm" :rules="rescheduleRules" label-width="96px">
        <el-form-item label="新日期" prop="appointmentDate">
          <el-date-picker
            v-model="rescheduleForm.appointmentDate"
            class="!w-full"
            type="date"
            value-format="YYYY-MM-DD"
          />
        </el-form-item>
        <el-form-item label="新时段" prop="appointmentTime">
          <el-select v-model="rescheduleForm.appointmentTime" class="!w-full">
            <el-option v-for="slot in timeSlots" :key="slot" :label="slot" :value="slot" />
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="rescheduleForm.remark" type="textarea" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button type="primary" @click="submitReschedule">确 定</el-button>
        <el-button @click="rescheduleVisible = false">取 消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ElMessageBox } from 'element-plus'
import { getO2oMerchantStorePage, type O2oStoreMerchantRespVO } from '@/api/merchant/o2o/store'
import {
  cancelO2oMerchantReservation,
  getO2oMerchantReservation,
  getO2oMerchantReservationPage,
  rescheduleO2oMerchantReservation,
  type O2oReservationMerchantRespVO
} from '@/api/merchant/o2o/reservation'

defineOptions({ name: 'MerchantO2oReservation' })

const message = useMessage()

const statusOptions = [
  { label: '待确认', value: 'PENDING' },
  { label: '已确认', value: 'CONFIRMED' },
  { label: '服务中', value: 'IN_SERVICE' },
  { label: '已完成', value: 'COMPLETED' },
  { label: '已取消', value: 'CANCELLED' }
]
const timeSlots = ['09:00-10:00', '10:00-11:00', '11:00-12:00', '14:00-15:00', '15:00-16:00', '16:00-17:00']

const loading = ref(true)
const list = ref<O2oReservationMerchantRespVO[]>([])
const total = ref(0)
const storeOptions = ref<O2oStoreMerchantRespVO[]>([])
const storeNameMap = computed(() =>
  Object.fromEntries(storeOptions.value.map((s) => [s.id, s.name]))
)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  orderId: '' as string,
  storeId: undefined as number | undefined,
  status: undefined as string | undefined,
  contactPhone: undefined as string | undefined
})
const queryFormRef = ref()

const detailVisible = ref(false)
const detailLoading = ref(false)
const detail = ref<O2oReservationMerchantRespVO>()

const rescheduleVisible = ref(false)
const rescheduleRef = ref()
const rescheduleForm = ref({
  id: 0,
  appointmentDate: '',
  appointmentTime: '',
  remark: ''
})
const rescheduleRules = {
  appointmentDate: [{ required: true, message: '请选择日期', trigger: 'change' }],
  appointmentTime: [{ required: true, message: '请选择时段', trigger: 'change' }]
}

const statusLabel = (s?: string) => statusOptions.find((o) => o.value === s)?.label || s || '—'
const statusTagType = (s?: string) => {
  if (s === 'CONFIRMED' || s === 'COMPLETED') return 'success'
  if (s === 'CANCELLED') return 'info'
  if (s === 'IN_SERVICE') return 'warning'
  return 'warning'
}

const buildQuery = () => {
  const params: Record<string, unknown> = {
    pageNo: queryParams.pageNo,
    pageSize: queryParams.pageSize,
    storeId: queryParams.storeId,
    status: queryParams.status || undefined,
    contactPhone: queryParams.contactPhone || undefined
  }
  if (queryParams.orderId?.trim()) {
    params.orderId = Number(queryParams.orderId)
  }
  return params
}

const loadStores = async () => {
  const data = await getO2oMerchantStorePage({ pageNo: 1, pageSize: 100 })
  storeOptions.value = data.list || []
}

const getList = async () => {
  loading.value = true
  try {
    const data = await getO2oMerchantReservationPage(buildQuery() as any)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  queryParams.orderId = ''
  handleQuery()
}

const openDetail = async (id: number) => {
  detailVisible.value = true
  detailLoading.value = true
  try {
    detail.value = await getO2oMerchantReservation(id)
  } finally {
    detailLoading.value = false
  }
}

const openReschedule = (row: O2oReservationMerchantRespVO) => {
  rescheduleForm.value = {
    id: row.id,
    appointmentDate: row.appointmentDate || '',
    appointmentTime: row.appointmentTime || '',
    remark: ''
  }
  rescheduleVisible.value = true
}

const submitReschedule = async () => {
  await rescheduleRef.value?.validate()
  await rescheduleO2oMerchantReservation(rescheduleForm.value)
  message.success('改期成功')
  rescheduleVisible.value = false
  await getList()
}

const handleCancel = async (row: O2oReservationMerchantRespVO) => {
  let remark = ''
  try {
    const { value } = await ElMessageBox.prompt('请输入取消原因（可选）', '取消预约', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      inputRequired: false
    })
    remark = value || ''
  } catch {
    return
  }
  await cancelO2oMerchantReservation({ id: row.id, remark })
  message.success('已取消')
  await getList()
}

onMounted(async () => {
  await loadStores()
  await getList()
})
</script>
